import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, CAD, stageClass, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Plus, ChevronLeft, ChevronRight, Building2, KeyRound, Pencil, Trash2, AlertTriangle, Upload,
  Search, LayoutGrid, List as ListIcon, Users, UserCheck, Sparkles, FileText, DollarSign,
  MapPin, Mail, Phone, Image as ImageIcon, X, ArrowUpDown, Trophy, Clock, Hash,
} from "lucide-react";
import { toast } from "sonner";
import PartnersImportDialog from "./PartnersImportDialog";
import PartnerLogo from "../components/PartnerLogo";

/* ------------------------------------------------------------------ */
/*  Partners directory                                                 */
/* ------------------------------------------------------------------ */

const TIER_STYLES = {
  Platinum: "bg-slate-900 text-white border-slate-900",
  Gold:     "bg-amber-50 text-amber-800 border-amber-200",
  Silver:   "bg-slate-100 text-slate-700 border-slate-200",
  Bronze:   "bg-orange-50 text-orange-800 border-orange-200",
  New:      "bg-sky-50 text-sky-700 border-sky-200",
};

const STATUS_STYLES = {
  Active:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  Inactive: "bg-slate-100 text-slate-600 border-slate-200",
  Archived: "bg-amber-50 text-amber-800 border-amber-200",
};

const SORTS = [
  { key: "created_desc",   label: "Newest first" },
  { key: "created_asc",    label: "Oldest first" },
  { key: "apps_desc",      label: "Most applications" },
  { key: "last_sub_desc",  label: "Last submission" },
  { key: "alpha_asc",      label: "A → Z" },
  { key: "alpha_desc",     label: "Z → A" },
];

const FILTERS = [
  { key: "all",       label: "All",            icon: Users },
  { key: "active",    label: "Active",         icon: UserCheck },
  { key: "inactive",  label: "Inactive",       icon: X },
  { key: "new",       label: "New",            icon: Sparkles },
  { key: "top",       label: "Top performing", icon: Trophy },
];

export function Partners() {
  const [rows, setRows] = useState([]);
  const [overview, setOverview] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("created_desc");
  const [view, setView] = useState(() => {
    const saved = localStorage.getItem("partners_view") || "compact";
    // Legacy migration: old 2-mode toggle used "grid"/"list" — map to the new names
    // so returning users keep the view they picked last time.
    if (saved === "grid") return "detailed";
    if (saved === "list") return "table";
    return saved;
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem("partners_page_size")) || 25);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bdmUsers, setBdmUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => { localStorage.setItem("partners_view", view); }, [view]);
  useEffect(() => { localStorage.setItem("partners_page_size", String(pageSize)); }, [pageSize]);
  // Any change to the result set must reset the pager — otherwise a user filtered
  // down to 4 rows while sitting on page 3 would see an empty screen.
  useEffect(() => { setPage(1); }, [q, filter, sort, pageSize]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, o] = await Promise.all([
        api.get(`/partners?q=${encodeURIComponent(q)}`),
        api.get("/partners/overview"),
      ]);
      setRows(p.data || []);
      setOverview(o.data);
    } finally { setLoading(false); }
  };
  // Debounce the search by 250ms so typing feels snappy.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  useEffect(() => {
    api.get("/meta").then(r => setBdmUsers((r.data.users || []).filter(u => ["bdm","admin","management"].includes(u.role))));
  }, []);

  // Derive the visible list — filter + sort client-side so interactions feel instant.
  const visible = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const activeStatuses = new Set(["Active"]);
    let list = [...rows];

    if (filter === "active")   list = list.filter(p => activeStatuses.has(p.status));
    if (filter === "inactive") list = list.filter(p => !activeStatuses.has(p.status));
    if (filter === "new")      list = list.filter(p => p.created_at && new Date(p.created_at) >= monthStart);
    if (filter === "top") {
      list = [...list].sort((a,b) => (b.total_apps||0) - (a.total_apps||0)).slice(0, 10);
    }

    const cmp = (a, b, k, dir=1) => {
      const av = a[k] ?? "", bv = b[k] ?? "";
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * dir;
    };
    switch (sort) {
      case "created_asc":   list.sort((a,b) => cmp(a,b,"created_at",  1)); break;
      case "apps_desc":     list.sort((a,b) => (b.total_apps||0) - (a.total_apps||0)); break;
      case "last_sub_desc": list.sort((a,b) => cmp(a,b,"last_submission_date", -1)); break;
      case "alpha_asc":     list.sort((a,b) => cmp(a,b,"company_name",  1)); break;
      case "alpha_desc":    list.sort((a,b) => cmp(a,b,"company_name", -1)); break;
      case "created_desc":
      default:              list.sort((a,b) => cmp(a,b,"created_at", -1)); break;
    }
    return list;
  }, [rows, filter, sort]);

  const filterCount = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      all: rows.length,
      active: rows.filter(p => p.status === "Active").length,
      inactive: rows.filter(p => p.status !== "Active").length,
      new: rows.filter(p => p.created_at && new Date(p.created_at) >= monthStart).length,
      top: Math.min(10, rows.length),
    };
  }, [rows]);

  // Pagination — clamp current page in case the filter shrunk the result set.
  const total = visible.length;
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, maxPage);
  const paged = visible.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="space-y-6" data-testid="partners-page">
      {/* Hero / header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">Partners Directory</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage every B2B partner relationship — logos, performance and portal access in one place.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="partners-import-btn" variant="outline" onClick={()=>setImportOpen(true)}>
            <Upload className="w-4 h-4 mr-2"/>Bulk Import
          </Button>
          <Button data-testid="partners-add-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-2"/>Add Partner
          </Button>
        </div>
      </div>

      {/* Summary KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI icon={Users}      label="Total Partners"            value={overview?.total_partners ?? "–"} tint="navy" testid="kpi-total" />
        <KPI icon={UserCheck}  label="Active Partners"           value={overview?.active_partners ?? "–"} tint="emerald" testid="kpi-active" />
        <KPI icon={Sparkles}   label="New This Month"            value={overview?.new_partners_this_month ?? "–"} tint="sky" testid="kpi-new" />
        <KPI icon={FileText}   label="Applications This Month"   value={overview?.applications_this_month ?? "–"} tint="violet" testid="kpi-apps-month" />
        <KPI icon={DollarSign} label="B2B Revenue This Month"    value={overview ? CAD(overview.revenue_this_month) : "–"} tint="orange" testid="kpi-revenue-month" />
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 md:p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="partners-search"
              placeholder="Search company, contact, email, phone or partner ID…"
              value={q}
              onChange={e=>setQ(e.target.value)}
              className="pl-9 bg-white"
            />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <div className="hidden md:flex items-center gap-1 text-xs text-slate-500">
              <ArrowUpDown className="w-3.5 h-3.5" /> Sort by
            </div>
            <select
              data-testid="partners-sort"
              value={sort} onChange={e=>setSort(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
              {[
                { k: "compact",  label: "Compact",  tid: "partners-view-compact"  },
                { k: "detailed", label: "Detailed", tid: "partners-view-detailed" },
                { k: "table",    label: "Table",    tid: "partners-view-table"    },
              ].map((o, i) => (
                <button
                  key={o.k}
                  data-testid={o.tid}
                  onClick={()=>setView(o.k)}
                  className={`px-3 h-9 flex items-center font-medium ${i>0?"border-l border-slate-200":""} ${view===o.k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  title={`${o.label} view`}
                >{o.label}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERS.map(f => {
            const Icon = f.icon;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                data-testid={`partners-filter-${f.key}`}
                onClick={()=>setFilter(f.key)}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium border transition-colors
                  ${active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-900 hover:text-slate-900"}`}
              >
                <Icon className="w-3.5 h-3.5"/>{f.label}
                <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{filterCount[f.key] ?? 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <SkeletonList view={view} />
      ) : visible.length === 0 ? (
        <EmptyState hasRows={rows.length > 0} onAdd={()=>setOpen(true)} />
      ) : (
        <>
          {view === "compact" ? (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3"
              data-testid="partners-grid-compact"
            >
              {paged.map(p => <CompactPartnerCard key={p.id} p={p} onOpen={()=>nav(`/partners/${p.id}`)} />)}
            </div>
          ) : view === "detailed" ? (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
              data-testid="partners-grid"
            >
              {paged.map(p => <PartnerCard key={p.id} p={p} onOpen={()=>nav(`/partners/${p.id}`)} />)}
            </div>
          ) : (
            <PartnersTable rows={paged} onOpen={(id)=>nav(`/partners/${id}`)} />
          )}
          <Pagination
            total={total}
            page={safePage}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={setPageSize}
          />
        </>
      )}

      <NewPartnerDialog open={open} onOpenChange={setOpen} bdmUsers={bdmUsers} onCreated={()=>{ setOpen(false); load(); }} />
      <PartnersImportDialog open={importOpen} onOpenChange={setImportOpen} bdmUsers={bdmUsers} onImported={load} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Grid card                                                          */
/* ------------------------------------------------------------------ */
function PartnerCard({ p, onOpen }) {
  const hasApps  = (p.total_apps ?? 0) > 0;
  const hasAny   = hasApps || p.last_submission_date;
  const location = [p.city, p.country].filter(Boolean).join(", ") || "Location not set";
  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e)=>{ if (e.key === "Enter") onOpen(); }}
      onClick={onOpen}
      data-testid={`partner-card-${p.partner_id}`}
      className="group relative bg-white rounded-xl border border-slate-200 p-5 cursor-pointer transition-all
                 hover:border-slate-900 hover:shadow-lg hover:-translate-y-0.5"
    >
      {/* Top row — logo + partner-id */}
      <div className="flex items-start justify-between">
        <PartnerLogo partner={p} size="lg" />
        <div className="flex flex-col items-end gap-1.5">
          <span className="mono text-[11px] text-slate-500 bg-slate-50 px-2 py-0.5 rounded">{p.partner_id}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wide ${TIER_STYLES[p.tier] || TIER_STYLES.New}`}>{p.tier}</span>
        </div>
      </div>
      {/* Identity */}
      <div className="mt-4">
        <div className="heading-font font-semibold text-slate-900 text-base leading-snug line-clamp-1">{p.company_name}</div>
        <div className="text-xs text-slate-600 mt-1 flex items-center gap-1 line-clamp-1">
          {p.contact_person
            ? <>{p.contact_person}</>
            : <span className="italic text-slate-400">No contact set</span>}
        </div>
        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1 line-clamp-1">
          <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0"/>{location}
        </div>
        {p.email && (
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1 line-clamp-1">
            <Mail className="w-3 h-3 text-slate-400 flex-shrink-0"/>{p.email}
          </div>
        )}
      </div>
      {/* Metrics strip */}
      <div className="mt-4 grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100 pt-3">
        <Metric label="Total apps"    value={p.total_apps ?? 0} />
        <Metric label="This month"    value={p.apps_this_month ?? 0} />
        <Metric label="Last submission" value={p.last_submission_date ? fmtDate(p.last_submission_date) : "—"} small />
      </div>
      {/* Footer */}
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[p.status] || STATUS_STYLES.Active}`}>{p.status || "Active"}</span>
        <span className="text-[11px] text-slate-500 truncate">
          {p.assigned_bdm_name
            ? <>BDM · <span className="text-slate-700">{p.assigned_bdm_name}</span></>
            : <span className="italic text-slate-400">Unassigned</span>}
        </span>
      </div>
      {!hasAny && (
        <div className="absolute inset-x-5 bottom-2 text-[10px] text-slate-400 italic text-right opacity-0 group-hover:opacity-100 transition-opacity">
          No applications yet
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, small }) {
  return (
    <div className="px-2 first:pl-0 last:pr-0">
      <div className={`heading-font font-bold text-slate-900 ${small ? "text-xs" : "text-base"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Compact card (default) — 170–190px tall, 4 per row on 2xl         */
/* ------------------------------------------------------------------ */
function CompactPartnerCard({ p, onOpen }) {
  const location = [p.city, p.country].filter(Boolean).join(", ");
  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e)=>{ if (e.key === "Enter") onOpen(); }}
      onClick={onOpen}
      data-testid={`partner-card-${p.partner_id}`}
      className="group bg-white rounded-xl border border-slate-200 p-3 cursor-pointer transition-all hover:border-slate-900 hover:shadow-md"
    >
      {/* Row 1 — logo + identity (name, contact, email). Height-budget: ~56px. */}
      <div className="flex items-start gap-3">
        <PartnerLogo partner={p} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="heading-font font-semibold text-slate-900 text-sm leading-tight truncate" title={p.company_name}>
              {p.company_name}
            </div>
            <span className="mono text-[10px] text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded flex-shrink-0">{p.partner_id}</span>
          </div>
          <div className="text-[11px] text-slate-600 truncate" title={p.contact_person || ""}>
            {p.contact_person || <span className="italic text-slate-400">No contact</span>}
          </div>
          <div className="text-[11px] text-slate-500 truncate" title={p.email || ""}>
            {p.email || <span className="italic text-slate-400">No email</span>}
          </div>
        </div>
      </div>
      {/* Row 2 — stats strip (3 numbers in a tight horizontal row) */}
      <div className="mt-2.5 grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100 pt-2">
        <CompactMetric label="Apps"       value={p.total_apps ?? 0} />
        <CompactMetric label="This month" value={p.apps_this_month ?? 0} />
        <CompactMetric label="Last"       value={p.last_submission_date ? fmtDate(p.last_submission_date) : "—"} small />
      </div>
      {/* Row 3 — status / tier pills + assigned BDM. Everything inline. */}
      <div className="mt-2 flex items-center justify-between gap-2 text-[10px]">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`px-1.5 py-0.5 rounded-full border ${STATUS_STYLES[p.status] || STATUS_STYLES.Active}`}>{p.status || "Active"}</span>
          <span className={`px-1.5 py-0.5 rounded-full border uppercase tracking-wide ${TIER_STYLES[p.tier] || TIER_STYLES.New}`}>{p.tier}</span>
          {location && <span className="text-slate-500 truncate inline-flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5 text-slate-400"/>{location}</span>}
        </div>
        <span className="text-slate-500 truncate" title={p.assigned_bdm_name || ""}>
          {p.assigned_bdm_name ? p.assigned_bdm_name : <span className="italic text-slate-400">Unassigned</span>}
        </span>
      </div>
    </div>
  );
}
function CompactMetric({ label, value, small }) {
  return (
    <div className="px-1.5 first:pl-0 last:pr-0">
      <div className={`heading-font font-bold text-slate-900 ${small ? "text-[11px]" : "text-sm"} leading-tight`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Pagination (page-size switch + prev/next, used by every view)      */
/* ------------------------------------------------------------------ */
function Pagination({ total, page, pageSize, onPage, onPageSize }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div
      data-testid="partners-pagination"
      className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl border border-slate-200 px-3 py-2 text-xs"
    >
      <div className="flex items-center gap-2 text-slate-600">
        <span>Showing <span className="font-semibold text-slate-900">{start}–{end}</span> of <span className="font-semibold text-slate-900">{total}</span></span>
        <span className="text-slate-300">·</span>
        <label className="flex items-center gap-1.5">
          Rows
          <select
            data-testid="partners-page-size"
            value={pageSize}
            onChange={(e)=>onPageSize(Number(e.target.value))}
            className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
      </div>
      <div className="flex items-center gap-1">
        <button
          data-testid="partners-page-prev"
          onClick={()=>onPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        ><ChevronLeft className="w-3.5 h-3.5"/>Prev</button>
        <span className="px-2 text-slate-500">Page <span className="font-semibold text-slate-900">{page}</span> / {pageCount}</span>
        <button
          data-testid="partners-page-next"
          onClick={()=>onPage(Math.min(pageCount, page + 1))}
          disabled={page >= pageCount}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >Next<ChevronRight className="w-3.5 h-3.5"/></button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  List view                                                          */
/* ------------------------------------------------------------------ */
function PartnersTable({ rows, onOpen }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="partners-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 w-[32%]">Partner</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Location</th>
              <th className="text-right px-4 py-3">Apps</th>
              <th className="text-right px-4 py-3">This month</th>
              <th className="text-left px-4 py-3">Last submission</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Tier</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(p => (
              <tr
                key={p.id}
                data-testid={`partner-row-${p.partner_id}`}
                onClick={()=>onOpen(p.id)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <PartnerLogo partner={p} size="sm" />
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{p.company_name}</div>
                      <div className="mono text-[11px] text-slate-500">{p.partner_id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  <div className="truncate max-w-[220px]">{p.contact_person || <span className="italic text-slate-400">—</span>}</div>
                  <div className="text-[11px] text-slate-500 truncate max-w-[220px]">{p.email || ""}</div>
                </td>
                <td className="px-4 py-3 text-slate-700 truncate">{[p.city, p.country].filter(Boolean).join(", ") || <span className="italic text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">{p.total_apps ?? 0}</td>
                <td className="px-4 py-3 text-right text-slate-700">{p.apps_this_month ?? 0}</td>
                <td className="px-4 py-3 text-slate-700">{p.last_submission_date ? fmtDate(p.last_submission_date) : <span className="italic text-slate-400">—</span>}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[p.status] || STATUS_STYLES.Active}`}>{p.status || "Active"}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${TIER_STYLES[p.tier] || TIER_STYLES.New}`}>{p.tier}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Chrome: KPI, Skeleton, Empty state                                 */
/* ------------------------------------------------------------------ */
const TINTS = {
  navy:    { ring: "ring-slate-900/10",   icon: "bg-slate-900 text-white" },
  emerald: { ring: "ring-emerald-500/10", icon: "bg-emerald-500 text-white" },
  sky:     { ring: "ring-sky-500/10",     icon: "bg-sky-500 text-white" },
  violet:  { ring: "ring-violet-500/10",  icon: "bg-violet-500 text-white" },
  orange:  { ring: "ring-orange-500/10",  icon: "bg-orange-500 text-white" },
};
function KPI({ icon: Icon, label, value, tint="navy", testid }) {
  const t = TINTS[tint] || TINTS.navy;
  return (
    <div data-testid={testid} className={`bg-white rounded-xl border border-slate-200 p-4 ring-1 ${t.ring}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${t.icon}`}><Icon className="w-4 h-4"/></div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 truncate">{label}</div>
          <div className="heading-font text-xl font-bold text-slate-900 leading-tight truncate">{value}</div>
        </div>
      </div>
    </div>
  );
}

function SkeletonList({ view }) {
  const cards = Array.from({ length: 6 });
  if (view === "list") {
    return (
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {cards.map((_, i) => (
          <div key={i} className="px-4 py-3 animate-pulse flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-100" />
            <div className="h-3 w-40 bg-slate-100 rounded" />
            <div className="h-3 w-24 bg-slate-100 rounded ml-auto" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {cards.map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
          <div className="w-14 h-14 bg-slate-100 rounded-lg" />
          <div className="mt-4 h-4 w-40 bg-slate-100 rounded" />
          <div className="mt-2 h-3 w-28 bg-slate-100 rounded" />
          <div className="mt-6 grid grid-cols-3 gap-2">
            <div className="h-6 bg-slate-100 rounded" />
            <div className="h-6 bg-slate-100 rounded" />
            <div className="h-6 bg-slate-100 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ hasRows, onAdd }) {
  if (hasRows) {
    return (
      <div className="bg-white border border-dashed border-slate-200 rounded-xl py-12 text-center">
        <div className="text-slate-600">No partners match this filter.</div>
        <div className="text-xs text-slate-400 mt-1">Try switching the filter or clearing your search.</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-dashed border-slate-200 rounded-xl py-14 text-center">
      <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-500 inline-flex items-center justify-center">
        <Building2 className="w-6 h-6"/>
      </div>
      <div className="heading-font text-slate-900 font-semibold mt-3">No partners yet</div>
      <div className="text-sm text-slate-500 mt-1">Add your first partner to start tracking applications and commissions.</div>
      <Button onClick={onAdd} className="mt-4 bg-orange-500 hover:bg-orange-600 text-white">
        <Plus className="w-4 h-4 mr-2"/>Add Partner
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Partner Detail (lightly enriched; same shell as before)            */
/* ------------------------------------------------------------------ */
export function PartnerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [portalOpen, setPortalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [bdmUsers, setBdmUsers] = useState([]);
  const load = () => api.get(`/partners/${id}`).then(r=>setData(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  useEffect(() => {
    api.get("/meta").then(r => setBdmUsers((r.data.users || []).filter(u => ["bdm","admin","management"].includes(u.role))));
  }, []);
  // Activity timeline — assembled from real applications only (no fabricated events).
  // Must be declared BEFORE any early return so hook order stays stable across renders.
  const applications = data?.applications || [];
  const timeline = useMemo(() => {
    const events = [];
    applications.forEach(a => {
      if (a.date_received)              events.push({ ts: a.date_received,              text: `New application · ${a.student_name}`, meta: a.application_id });
      if (a.application_submitted_date) events.push({ ts: a.application_submitted_date, text: `Submitted · ${a.student_name}`,       meta: a.college });
      if (a.offer_received_date)        events.push({ ts: a.offer_received_date,        text: `Offer received · ${a.student_name}`,  meta: a.college });
      if (a.fees_paid_date)             events.push({ ts: a.fees_paid_date,             text: `Fees paid · ${a.student_name}`,       meta: a.application_id });
      if (a.pal_issued_date)            events.push({ ts: a.pal_issued_date,            text: `PAL issued · ${a.student_name}`,      meta: a.application_id });
    });
    return events.filter(e => e.ts).sort((a,b) => (a.ts > b.ts ? -1 : 1)).slice(0, 15);
  }, [applications]);
  if (!data) return <div>Loading…</div>;
  const { partner: p, stats } = data;
  const canEdit = ["admin", "bdm", "management"].includes(user?.role);
  const canDelete = user?.role === "admin";

  return (
    <div className="space-y-6">
      <Link to="/partners" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ChevronLeft className="w-4 h-4" /> Back to partners</Link>
      {/* Hero */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-4">
            <PartnerLogo partner={p} size="xl" />
            <div>
              <h1 className="heading-font text-2xl font-bold text-slate-900">{p.company_name}</h1>
              <div className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                {p.contact_person && <span>{p.contact_person}</span>}
                {p.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3"/>{p.email}</span>}
                {p.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{p.phone}</span>}
              </div>
              <div className="text-sm text-slate-500 mt-1 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3"/>{[p.city, p.country].filter(Boolean).join(", ") || "Location not set"}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="mono text-xs text-slate-500 bg-slate-50 px-2 py-0.5 rounded">{p.partner_id}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wide ${TIER_STYLES[p.tier] || TIER_STYLES.New}`}>{p.tier || "New"}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[p.status] || STATUS_STYLES.Active}`}>{p.status || "Active"}</span>
                {p.assigned_bdm_name ? (
                  <span className="text-xs px-2 py-1 rounded-full bg-slate-900 text-white inline-flex items-center gap-1" data-testid="partner-assigned-bdm">
                    <UserCheck className="w-3 h-3"/>BDM · {p.assigned_bdm_name}
                  </span>
                ) : (
                  <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 inline-flex items-center gap-1" data-testid="partner-assigned-bdm-missing">
                    <AlertTriangle className="w-3 h-3"/>Unassigned — click Edit to pick a BDM
                  </span>
                )}
                {p.is_portal_enabled ? (
                  <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Portal enabled · {p.login_email || "no login email"}</span>
                ) : (
                  <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Portal disabled</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && <Button data-testid="partner-edit-btn" variant="outline" onClick={()=>setEditOpen(true)}><Pencil className="w-4 h-4 mr-2"/>Edit</Button>}
            <Button data-testid="portal-access-btn" variant="outline" onClick={()=>setPortalOpen(true)}><KeyRound className="w-4 h-4 mr-2"/>Portal Access</Button>
            {canDelete && <Button data-testid="partner-delete-btn" variant="outline" onClick={()=>setDelOpen(true)} className="text-rose-700 border-rose-200 hover:bg-rose-50"><Trash2 className="w-4 h-4 mr-2"/>Delete</Button>}
          </div>
        </div>
      </div>

      {/* KPIs — real stats from backend */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat label="Total Applications" value={stats.total_applications} />
        <Stat label="Offers Received" value={stats.offers_received} />
        <Stat label="Students Enrolled" value={stats.students_enrolled} />
        <Stat label="Commission Generated" value={CAD(stats.commission_generated)} />
        <Stat label="Paid to Partner" value={CAD(stats.commission_paid_to_partner)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Applications table */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 heading-font font-semibold flex items-center justify-between">
            Applications
            <span className="text-xs text-slate-500 font-normal">{applications.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr><th className="text-left px-4 py-3">ID</th><th className="text-left px-4 py-3">Student</th><th className="text-left px-4 py-3">College</th><th className="text-left px-4 py-3">Stage</th><th className="text-left px-4 py-3">Date</th></tr>
              </thead>
              <tbody>
                {applications.map(a => (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={()=>window.location.assign(`/applications/${a.id}`)}>
                    <td className="px-4 py-3 mono text-xs">{a.application_id}</td>
                    <td className="px-4 py-3 font-medium">{a.student_name}</td>
                    <td className="px-4 py-3">{a.college}</td>
                    <td className="px-4 py-3"><span className={`stage-badge ${stageClass(a.stage)}`}>{a.stage}</span></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(a.date_received)}</td>
                  </tr>
                ))}
                {applications.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic">No applications from this partner yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        {/* Activity timeline */}
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 heading-font font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4"/> Recent activity
          </div>
          <div className="p-4">
            {timeline.length === 0 ? (
              <div className="text-sm text-slate-400 italic text-center py-6">No activity yet</div>
            ) : (
              <ol className="relative border-l border-slate-200 pl-4 space-y-4">
                {timeline.map((e, i) => (
                  <li key={i} className="relative">
                    <div className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-slate-900 ring-4 ring-white"></div>
                    <div className="text-sm text-slate-800">{e.text}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{fmtDate(e.ts)}{e.meta ? ` · ${e.meta}` : ""}</div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>

      <PortalAccessDialog open={portalOpen} onOpenChange={setPortalOpen} partner={p} onSaved={()=>{ setPortalOpen(false); load(); }} />
      <EditPartnerDialog open={editOpen} onOpenChange={setEditOpen} partner={p} bdmUsers={bdmUsers} onSaved={()=>{ setEditOpen(false); load(); }} />
      <DeletePartnerDialog open={delOpen} onOpenChange={setDelOpen} partner={p} appCount={stats.total_applications} onDeleted={()=>nav("/partners")} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Dialogs (preserved + logo uploader added)                          */
/* ------------------------------------------------------------------ */
function EditPartnerDialog({ open, onOpenChange, partner, bdmUsers = [], onSaved }) {
  const [f, setF] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (open && partner) {
      // eslint-disable-next-line no-unused-vars
      const { id, partner_id, created_at, password_hash, ...editable } = partner;
      setF(editable);
    }
  }, [open, partner]);
  const set = (k, v) => setF(o => ({ ...o, [k]: v }));
  const submit = async () => {
    if (!f.company_name) { toast.error("Company name required"); return; }
    try { await api.patch(`/partners/${partner.id}`, f); toast.success("Partner updated"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const pickLogo = () => fileRef.current?.click();
  const onFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (file.size > 2_000_000) { toast.error("Logo must be ≤ 2 MB"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post(`/partners/${partner.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF(o => ({ ...o, logo_url: r.data.logo_url }));
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const clearLogo = async () => {
    try {
      await api.delete(`/partners/${partner.id}/logo`);
      setF(o => ({ ...o, logo_url: null }));
      toast.success("Logo cleared");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  // Preview partner reflects the in-dialog form so the user sees the new logo immediately.
  const previewPartner = { ...partner, ...f };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl">
        <DialogHeader><DialogTitle className="heading-font">Edit Partner — {partner?.partner_id}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 border border-slate-100">
          <PartnerLogo partner={previewPartner} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{previewPartner.company_name || "Logo preview"}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {previewPartner.logo_url
                ? "Custom logo uploaded"
                : (previewPartner.email && !["gmail.com","yahoo.com","hotmail.com","outlook.com","icloud.com"].includes((previewPartner.email.split("@")[1] || "").toLowerCase())
                    ? "Auto-sourced from company domain · upload to override"
                    : "Using initials avatar · upload a logo to brand this partner")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="partner-logo-file-input" />
            <Button type="button" variant="outline" onClick={pickLogo} disabled={uploading} data-testid="partner-logo-upload-btn">
              <ImageIcon className="w-4 h-4 mr-2"/>{uploading ? "Uploading…" : "Upload"}
            </Button>
            {previewPartner.logo_url && (
              <Button type="button" variant="outline" onClick={clearLogo} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="partner-logo-clear-btn">
                <X className="w-4 h-4"/>
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldL label="Company Name *"><Input data-testid="partner-edit-name" value={f.company_name||""} onChange={e=>set("company_name", e.target.value)} /></FieldL>
          <FieldL label="Contact Person"><Input value={f.contact_person||""} onChange={e=>set("contact_person", e.target.value)} /></FieldL>
          <FieldL label="Email"><Input value={f.email||""} onChange={e=>set("email", e.target.value)} /></FieldL>
          <FieldL label="Phone"><Input value={f.phone||""} onChange={e=>set("phone", e.target.value)} /></FieldL>
          <FieldL label="City"><Input value={f.city||""} onChange={e=>set("city", e.target.value)} /></FieldL>
          <FieldL label="Country"><Input value={f.country||""} onChange={e=>set("country", e.target.value)} /></FieldL>
          <FieldL label="Commission Agreement"><Input value={f.commission_agreement||""} onChange={e=>set("commission_agreement", e.target.value)} /></FieldL>
          <FieldL label="Assigned BDM">
            <select
              data-testid="partner-edit-assigned-bdm"
              value={f.assigned_bdm_id || ""}
              onChange={e=>set("assigned_bdm_id", e.target.value || null)}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            >
              <option value="">— Unassigned —</option>
              {bdmUsers.map(u => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name || u.email} · {u.role}
                </option>
              ))}
            </select>
          </FieldL>
          <FieldL label="Status">
            <select
              value={f.status || "Active"}
              onChange={e=>set("status", e.target.value)}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            >
              <option>Active</option><option>Inactive</option><option>Archived</option>
            </select>
          </FieldL>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button data-testid="partner-edit-save" onClick={submit} className="bg-orange-500 hover:bg-orange-600 text-white">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeletePartnerDialog({ open, onOpenChange, partner, appCount, onDeleted }) {
  const [confirm, setConfirm] = useState("");
  const [mode, setMode] = useState("archive"); // archive | hard
  useEffect(() => { if (open) { setConfirm(""); setMode("archive"); } }, [open]);
  const hasApps = appCount > 0;
  const canConfirm = confirm.trim().toUpperCase() === "DELETE";
  const submit = async () => {
    if (!canConfirm) { toast.error('Type DELETE to confirm'); return; }
    let query = "";
    if (hasApps) query = mode === "hard" ? "?hard=true" : "?force=true";
    try {
      const r = await api.delete(`/partners/${partner.id}${query}`);
      const label = r.data.action === "archived"
        ? `Partner archived (kept ${appCount} application record${appCount===1?"":"s"})`
        : r.data.action === "hard_deleted"
          ? `Partner permanently deleted · ${appCount} application${appCount===1?"":"s"} unlinked`
          : "Partner deleted";
      toast.success(label);
      onDeleted();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="heading-font flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5"/> Delete partner</DialogTitle>
          <DialogDescription className="pt-2">
            You are about to remove <span className="font-semibold text-slate-900">{partner?.company_name}</span> ({partner?.partner_id}).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {hasApps ? (
            <div className="space-y-2">
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                This partner has <span className="font-semibold">{appCount}</span> application{appCount===1?"":"s"}. Choose how to proceed:
              </div>
              <label className="flex items-start gap-2 p-2 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <input data-testid="partner-delete-mode-archive" type="radio" name="delmode" className="mt-1" checked={mode==="archive"} onChange={()=>setMode("archive")} />
                <div className="text-xs">
                  <div className="font-semibold text-slate-900">Archive (recommended)</div>
                  <div className="text-slate-600">Partner is hidden from active lists, portal disabled. Applications keep their partner link so history + reports stay intact.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 p-2 rounded-md border border-rose-200 hover:bg-rose-50 cursor-pointer">
                <input data-testid="partner-delete-mode-hard" type="radio" name="delmode" className="mt-1" checked={mode==="hard"} onChange={()=>setMode("hard")} />
                <div className="text-xs">
                  <div className="font-semibold text-rose-700">Delete permanently</div>
                  <div className="text-rose-800">Partner row is removed. The {appCount} application{appCount===1?"":"s"} stay but their partner link is cleared. This cannot be undone.</div>
                </div>
              </label>
            </div>
          ) : (
            <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-xs text-rose-900">
              No applications are linked. The partner will be <span className="font-semibold">permanently deleted</span>.
            </div>
          )}
          <FieldL label={<span>Type <span className="mono font-bold">DELETE</span> to confirm</span>}>
            <Input data-testid="partner-delete-confirm" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="DELETE" />
          </FieldL>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button data-testid="partner-delete-confirm-btn" onClick={submit} disabled={!canConfirm} className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50">
            {hasApps ? (mode === "hard" ? "Delete Permanently" : "Archive Partner") : "Delete Partner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PortalAccessDialog({ open, onOpenChange, partner, onSaved }) {
  const [loginEmail, setLoginEmail] = useState("");
  const [password, setPassword] = useState("");
  const [enabled, setEnabled] = useState(true);
  useEffect(() => {
    if (open) {
      setLoginEmail(partner?.login_email || partner?.email || "");
      setPassword("");
      setEnabled(partner?.is_portal_enabled !== false);
    }
  }, [open, partner]);
  const submit = async () => {
    if (!loginEmail) { toast.error("Login email required"); return; }
    try {
      const r = await api.post(`/partners/${partner.id}/portal-access`, { login_email: loginEmail, password, is_portal_enabled: enabled });
      const invited = r.data?.invite_email_sent;
      toast.success(password ? (invited ? "Portal credentials updated · invite email sent" : "Portal credentials updated") : "Portal settings saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="heading-font">Partner Portal Access</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <FieldL label="Login Email"><Input data-testid="portal-login-email" value={loginEmail} onChange={e=>setLoginEmail(e.target.value)} placeholder="partner@company.com" /></FieldL>
          <FieldL label={partner?.password_hash ? "New Password (leave blank to keep current)" : "Set Password (min 8 chars)"}>
            <Input data-testid="portal-password" type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" />
          </FieldL>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input data-testid="portal-enabled" type="checkbox" checked={enabled} onChange={e=>setEnabled(e.target.checked)} />
            Portal access enabled
          </label>
          {password && enabled && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
              An invite email with the login URL, email, and this password will be sent to <span className="mono">{loginEmail || "the partner"}</span> when you save.
            </div>
          )}
          <p className="text-xs text-slate-500">The partner logs in at <span className="mono">/partner/login</span>. Changing password logs out active sessions.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button data-testid="portal-save" onClick={submit} className="bg-orange-500 hover:bg-orange-600 text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className="heading-font text-xl font-bold text-slate-900 mt-1">{value}</div>
  </div>
);

function NewPartnerDialog({ open, onOpenChange, bdmUsers = [], onCreated }) {
  const [f, setF] = useState({});
  const set = (k, v) => setF(o => ({ ...o, [k]: v }));
  const submit = async () => {
    if (!f.company_name) { toast.error("Company name required"); return; }
    try { await api.post("/partners", f); toast.success("Partner created · open the partner to upload a logo"); setF({}); onCreated(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="heading-font">Add Partner</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Create the record first — once saved, open the partner and upload a logo (we'll try their company domain automatically meanwhile).
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldL label="Company Name *"><Input data-testid="partner-name-input" value={f.company_name||""} onChange={e=>set("company_name", e.target.value)} /></FieldL>
          <FieldL label="Contact Person"><Input value={f.contact_person||""} onChange={e=>set("contact_person", e.target.value)} /></FieldL>
          <FieldL label="Email"><Input value={f.email||""} onChange={e=>set("email", e.target.value)} /></FieldL>
          <FieldL label="Phone"><Input value={f.phone||""} onChange={e=>set("phone", e.target.value)} /></FieldL>
          <FieldL label="City"><Input value={f.city||""} onChange={e=>set("city", e.target.value)} /></FieldL>
          <FieldL label="Country"><Input value={f.country||""} onChange={e=>set("country", e.target.value)} /></FieldL>
          <FieldL label="Commission Agreement"><Input value={f.commission_agreement||""} onChange={e=>set("commission_agreement", e.target.value)} /></FieldL>
          <FieldL label="Assigned BDM">
            <select
              data-testid="partner-new-assigned-bdm"
              value={f.assigned_bdm_id || ""}
              onChange={e=>set("assigned_bdm_id", e.target.value || null)}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            >
              <option value="">— Unassigned —</option>
              {bdmUsers.map(u => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name || u.email} · {u.role}
                </option>
              ))}
            </select>
          </FieldL>
          <FieldL label="Status">
            <select
              value={f.status || "Active"}
              onChange={e=>set("status", e.target.value)}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            >
              <option>Active</option><option>Inactive</option><option>Archived</option>
            </select>
          </FieldL>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button data-testid="partner-submit-btn" onClick={submit} className="bg-orange-500 hover:bg-orange-600 text-white">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
const FieldL = ({label, children}) => (<div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>);
